import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { REACT_APP_GOOGLE_CLIENT_ID, AUTH_RESEND_VERIFICATION, AUTH_GOOGLE, STUDENT_RESEND_PARENT_CONSENT } from '../config/Config';
import AuthContext from '../contexts/AuthContext';
import { clearForFreshLogin } from '../utils/authStorage';
import { showLoginFailureAlert } from '../utils/authFormErrors';
import { Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../services/api';
import NavBar from '../components/common/NavBar';

import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
  FormControlLabel,
  Checkbox,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

export const GoogleLoginButton = ({ onParentConsentBlocked }) => {
  const navigate = useNavigate();

  const isParentConsentBlockedMessage = (message) =>
    typeof message === 'string' && message.toLowerCase().includes('parent/guardian consent');

  useEffect(() => {
    const interval = setInterval(() => {
      const google = window.google;
      const buttonEl = document.getElementById("google-login-btn");

      if (google && buttonEl) {
        google.accounts.id.initialize({
          client_id: REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        });

        google.accounts.id.renderButton(buttonEl, {
          theme: "outline",
          size: "large",
          width: 280,
        });

        clearInterval(interval);
      }
    }, 300);

    return () => clearInterval(interval);
  }, []);

  const handleCredentialResponse = async (response) => {
    const id_token = response.credential;
  
    try {
      const res = await api.post(AUTH_GOOGLE, { token: id_token });
      const data = res.data;
  
      if (res.status === 200) {
        localStorage.setItem("token", data.token);
        const user = data.user;
        const needsGrade = user?.requires_grade_selection;
        const needsProfile = user?.requires_profile_completion;
        if (needsProfile || needsGrade) {
          navigate("/complete-profile", { replace: true });
        } else {
          navigate("/dashboard");
        }
      } else if (res.status === 403) {
        if (data.error?.toLowerCase().includes('awaiting approval')) {
          alert("Awaiting approval. Please wait for your account to be activated.");
        } else if (data.requires_parent_consent || isParentConsentBlockedMessage(data.error)) {
          onParentConsentBlocked?.(data.error);
        } else if (data.error?.toLowerCase().includes('account is inactive')) {
          alert(data.error);
          // Redirect to pricing page for inactive accounts
          navigate(data.redirect || "/get-started");
        } else {
          alert("Not Registered yet. Redirecting to registration");
          navigate("/get-started");
        }
      } else {
        alert(data.error || "Google login failed.");
      }
    } catch (error) {
      alert("Not Registered yet. Redirecting to registration");
      // Redirect non-registered users to pricing page
      navigate("/get-started");
    }
  };

  return <div id="google-login-btn"></div>;
};

const Login = ({ onError }) => {
  const { login, requestLoginOtp, loginWithOtp, checkExistingSession } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loginMode, setLoginMode] = useState('otp');
  const [otpRequested, setOtpRequested] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);
  const [parentConsentDialogOpen, setParentConsentDialogOpen] = useState(false);
  const [parentConsentMessage, setParentConsentMessage] = useState('');
  const [resendingParentConsent, setResendingParentConsent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isParentConsentBlockedMessage = (message) =>
    typeof message === 'string' && message.toLowerCase().includes('parent/guardian consent');

  const openParentConsentDialog = (message) => {
    setParentConsentMessage(message || 'We need parent or guardian consent before you can log in.');
    setParentConsentDialogOpen(true);
  };

  // Clear corrupt cookies/storage on mount so login can succeed after browser close
  useEffect(() => {
    clearForFreshLogin();
  }, []);

  useEffect(() => {
    // Check for success message in location state
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the location state to prevent showing the message again on refresh
      window.history.replaceState({}, document.title);
    }
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location]);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/dashboard');
    }
  }, [navigate]);

  // Auto-login effect for existing sessions
  useEffect(() => {
    const attemptAutoLogin = async () => {
      // Only attempt auto-login if we're not already authenticated
      const token = localStorage.getItem('token');
      if (token) {
        return; // Already authenticated
      }

      // Check if we have a stored email from previous login
      const lastLoginEmail = localStorage.getItem('lastLoginEmail');
      if (!lastLoginEmail) {
        return; // No previous login found
      }

      try {
        console.log('[Login] Attempting auto-login for:', lastLoginEmail);
        
        // Check for existing session using the global function
        if (window.handleGlobalSessionConflict) {
          // Use the global session conflict handler
          const sessionResult = await checkExistingSession(lastLoginEmail);
          
          if (sessionResult.success && sessionResult.autoLogin) {
            console.log('[Login] Auto-login successful, redirecting to dashboard');
            navigate('/dashboard', { replace: true });
          } else if (sessionResult.success && (sessionResult.sameIP || sessionResult.sameDevice)) {
            console.log('[Login] Same device or no conflict, proceeding with login');
            // The user can proceed with normal login
          } else if (sessionResult.sessionConflict) {
            console.log('[Login] Session conflict detected, showing dialog');
            // Trigger the global session conflict dialog
            window.handleGlobalSessionConflict({
              ...sessionResult,
              email: lastLoginEmail
            });
          }
        }
      } catch (error) {
        console.error('[Login] Auto-login error:', error);
        // Don't show error to user, just log it
      }
    };

    // Attempt auto-login after a short delay to ensure auth context is ready
    const timer = setTimeout(attemptAutoLogin, 500);
    return () => clearTimeout(timer);
  }, [navigate, checkExistingSession]);

  // Expose global login retry function
  useEffect(() => {
    window.triggerLoginRetry = () => {
      console.log('[Login] Triggering login retry');
      // Clear any existing auth data
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      
      // Reload the page to restart the login process
      window.location.reload();
    };
    
    return () => {
      delete window.triggerLoginRetry;
    };
  }, []);

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const res = await api.post(AUTH_RESEND_VERIFICATION, { email });
              const data = res.data;
        if (res.status === 200) {
        setSuccessMessage('Verification email sent. Please check your inbox.');
      } else {
        setError(data.error || 'Failed to resend verification email.');
      }
    } catch (err) {
      setError('Network error. Please try again later.');
    } finally {
      setResending(false);
    }
  };

  const handleResendParentConsent = async () => {
    setResendingParentConsent(true);
    setError('');
    setSuccessMessage('');
    try {
      const res = await api.post(STUDENT_RESEND_PARENT_CONSENT, { email });
      setSuccessMessage(res.data?.message || 'We sent a fresh parent consent email.');
      setParentConsentDialogOpen(false);
    } catch (err) {
      setParentConsentMessage(
        err?.response?.data?.error || 'We could not resend the consent email right now. Please try again.'
      );
    } finally {
      setResendingParentConsent(false);
    }
  };

  const switchMode = (mode) => {
    setLoginMode(mode);
    setOtp('');
    setOtpRequested(false);
    setError('');
    setSuccessMessage('');
    setShowResend(false);
  };

  const handleRequestOtp = async () => {
    try {
      await requestLoginOtp(email);
      setOtp('');
      setOtpRequested(true);
      setSuccessMessage('We sent a 6-digit login code to your email.');
    } catch (apiError) {
      let errorMessage = 'Unable to send login code right now.';
      const backendError = apiError?.response?.data?.error;
      const backendDetail = apiError?.response?.data?.detail;
      errorMessage = backendError || backendDetail || apiError.message || errorMessage;

      if (
        apiError?.response?.data?.requires_parent_consent ||
        isParentConsentBlockedMessage(errorMessage)
      ) {
        openParentConsentDialog(errorMessage);
        return;
      }

      throw new Error(errorMessage);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!(loginMode === 'otp' && otpRequested)) {
      setSuccessMessage('');
    }
    setIsLoading(true);
    
    try {
      if (!email) {
        throw new Error('Email is required');
      }

      if (loginMode === 'otp' && !otpRequested) {
        await handleRequestOtp();
        setIsLoading(false);
        return;
      }

      if (loginMode === 'otp' && !otp) {
        throw new Error('Email login code is required');
      }

      if (loginMode === 'password' && !password) {
        throw new Error('Password is required');
      }
      
      // Clear any existing auth data
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      // Clear any existing auth data
      
      // Call the login function from AuthContext
      const result = loginMode === 'otp'
        ? await loginWithOtp(email, otp, rememberMe)
        : await login(email, password, rememberMe);
      
      if (result.success) {
        setIsLoading(false);
        const user = result.user;
        const needsGrade = user?.requires_grade_selection;
        const needsProfile = user?.requires_profile_completion;
        const requestedPath = location.state?.from;
        const redirectPath = typeof requestedPath === 'string' && requestedPath.startsWith('/')
          ? requestedPath
          : '/dashboard';
        if (needsProfile || needsGrade) {
          navigate('/complete-profile', { replace: true });
        } else {
          navigate(redirectPath, { replace: true });
        }
        return;
      } else {
        // Check if this is a session conflict
        if (result.sessionConflict) {
          console.log('[Login] Session conflict detected during login, showing dialog');
          // Trigger the global session conflict dialog
          if (window.handleGlobalSessionConflict) {
            window.handleGlobalSessionConflict({
              ...result,
              email: email
            });
          }
          setIsLoading(false);
          return;
        }

        if (result.requiresParentConsent || isParentConsentBlockedMessage(result.message)) {
          openParentConsentDialog(result.message);
          setIsLoading(false);
          return;
        }
        
        showLoginFailureAlert(setError, result.message);

        if (result.redirect && (result.message?.includes('inactive') || result.message?.includes('re-register') || result.message?.includes('subscription is inactive'))) {
          setTimeout(() => {
            navigate(result.redirect);
          }, 2000);
        }

        setIsLoading(false);
        return;
      }
      
    } catch (apiError) {
      let errorMessage = 'Login failed. Please check your credentials and try again.';
      
      // Check for Google user error specifically
      if (apiError.response?.data) {
        const { data } = apiError.response;
        
        // For Google users who haven't verified email, use the backend message
        if (data.error && data.error.includes('Sign in with google below')) {
          errorMessage = data.error;
        } else if (data.social_provider === 'google') {
          // Use the backend message if available, otherwise fallback to generic message
          errorMessage = data.error || 'You are registered with Google. Please login using Google.';
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.non_field_errors && data.non_field_errors.length > 0) {
          errorMessage = data.non_field_errors[0];
        } else if (data.error) {
          errorMessage = data.error;
        } else if (typeof data === 'string') {
          errorMessage = data;
        }
      } else if (apiError.message && apiError.message.includes('Google')) {
        errorMessage = 'You are registered with Google. Please login using Google.';
      } else if (apiError.message) {
        errorMessage = apiError.message;
      }
      
      showLoginFailureAlert(setError, errorMessage);
      if (isParentConsentBlockedMessage(errorMessage)) {
        openParentConsentDialog(errorMessage);
      }
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #667eea 50%, #764ba2 75%, #f093fb 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(30, 60, 114, 0.1) 0%, rgba(42, 82, 152, 0.1) 25%, rgba(102, 126, 234, 0.1) 50%, rgba(118, 75, 162, 0.1) 75%, rgba(240, 147, 251, 0.1) 100%)',
        zIndex: 0
      }
    }}>
      <Box sx={{ position: 'relative', zIndex: 10 }}>
        <NavBar />
      </Box>
      
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          position: 'relative',
          zIndex: 1
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 440,
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(255, 255, 255, 0.15)',
            p: { xs: 4, sm: 5 },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            position: 'relative',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              background: 'linear-gradient(90deg, #1e3c72 0%, #667eea 50%, #f093fb 100%)',
            }
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 5, mt: 2 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: '900',
                background: 'linear-gradient(45deg, #1e3c72, #2a5298)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem' }
              }}
            >
              Welcome Back!
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                fontSize: '1.1rem',
                lineHeight: 1.6
              }}
            >
              {loginMode === 'otp'
                ? 'Sign in with a one-time code sent to your email'
                : 'Sign in with your password'}
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                type="button"
                variant={loginMode === 'otp' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => switchMode('otp')}
                sx={{ borderRadius: 999, textTransform: 'none', px: 2.5 }}
              >
                Email OTP
              </Button>
              <Button
                type="button"
                variant={loginMode === 'password' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => switchMode('password')}
                sx={{ borderRadius: 999, textTransform: 'none', px: 2.5 }}
              >
                Use Password Instead
              </Button>
            </Box>
          </Box>

          {successMessage && (
            <Alert severity="success" sx={{ width: '100%', mb: 3, borderRadius: 2 }}>
              {successMessage}
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {showResend && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              Didn't receive the verification email?
              <Button
                onClick={handleResendVerification}
                size="small"
                sx={{ ml: 2 }}
                disabled={resending}
              >
                {resending ? 'Resending...' : 'Resend Email'}
              </Button>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              variant="outlined"
              margin="normal"
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              inputProps={{ 'data-testid': 'login-email' }}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999,
                  backgroundColor: 'rgba(255, 255, 255, 0.96)',
                },
                '& input:-webkit-autofill,& input:-webkit-autofill:hover,& input:-webkit-autofill:focus': {
                  WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.96) inset',
                  WebkitTextFillColor: 'inherit',
                  caretColor: 'inherit',
                  borderRadius: 999,
                },
              }}
            />

            {loginMode === 'password' ? (
              <>
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  inputProps={{ 'data-testid': 'login-password' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 999,
                      backgroundColor: 'rgba(255, 255, 255, 0.96)',
                    },
                    '& input:-webkit-autofill,& input:-webkit-autofill:hover,& input:-webkit-autofill:focus': {
                      WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.96) inset',
                      WebkitTextFillColor: 'inherit',
                      caretColor: 'inherit',
                      borderRadius: 999,
                    },
                  }}
                />

                <Typography variant="body2" sx={{ mt: 1, mb: 3 }}>
                  <MuiLink
                    component={RouterLink}
                    to="/forgot-password"
                    underline="hover"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      '&:hover': {
                        color: 'primary.dark'
                      }
                    }}
                  >
                    Forgot Password?
                  </MuiLink>
                </Typography>
              </>
            ) : (
              <Box sx={{ mb: 3 }}>
                {otpRequested ? (
                  <TextField
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    label="Email login code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{
                      mb: 1.5,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 999,
                        backgroundColor: 'rgba(255, 255, 255, 0.96)',
                      },
                    }}
                  />
                ) : null}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {otpRequested
                    ? 'Enter the 6-digit code we sent to your email. The code expires in 10 minutes.'
                    : 'We will send a one-time login code to your email address.'}
                </Typography>
                {otpRequested ? (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      type="button"
                      onClick={async () => {
                        setError('');
                        setSuccessMessage('');
                        setIsLoading(true);
                        try {
                          await handleRequestOtp();
                        } catch (apiError) {
                          let errorMessage = 'Unable to send login code right now.';
                          if (apiError.response?.data?.error) {
                            errorMessage = apiError.response.data.error;
                          } else if (apiError.response?.data?.detail) {
                            errorMessage = apiError.response.data.detail;
                          } else if (apiError.message) {
                            errorMessage = apiError.message;
                          }
                          setError(errorMessage);
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      disabled={isLoading}
                      variant="outlined"
                      sx={{
                        textTransform: 'none',
                        borderRadius: 999,
                        px: 3,
                        py: 1,
                        fontWeight: 600,
                        color: 'primary.main',
                        borderColor: 'rgba(63, 81, 181, 0.28)',
                        backgroundColor: 'rgba(255, 255, 255, 0.4)',
                        boxShadow: '0 6px 16px rgba(63, 81, 181, 0.12)',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'rgba(63, 81, 181, 0.08)',
                        },
                      }}
                    >
                      {isLoading ? 'Resending...' : 'Resend code'}
                    </Button>
                  </Box>
                ) : null}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'center', mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main'
                      }
                    }}
                  />
                }
                label="Remember me"
                sx={{ '& .MuiTypography-root': { fontSize: '0.875rem', fontWeight: 500 } }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={isLoading}
              data-testid="login-submit"
              sx={{
                mt: 2,
                mb: 3,
                py: 1.5,
                borderRadius: '50px',
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                background: 'linear-gradient(to right, #FF6A3D, #FF9E4F, #FFD15C)',
                boxShadow: '0 8px 25px rgba(255, 106, 61, 0.4)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 35px rgba(255, 106, 61, 0.5)',
                  background: 'linear-gradient(to right, #FF5A2D, #FF8E3F, #FFC14C)'
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  transform: 'none',
                  boxShadow: 'none'
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  {loginMode === 'otp' && !otpRequested ? 'Sending Code...' : 'Signing In...'}
                </>
              ) : (loginMode === 'otp' && !otpRequested ? 'Send Login Code' : 'Sign In')}
            </Button>
          </Box>

          <Divider sx={{ 
            my: 4, 
            color: 'text.secondary', 
            '&:before, &:after': { 
              borderColor: 'rgba(102, 126, 234, 0.2)' 
            } 
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              or
            </Typography>
          </Divider>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={4} />
            <Grid item xs={4}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '42px',
                  width: '100%',
                }}
              >
                <GoogleLoginButton onParentConsentBlocked={openParentConsentDialog} />
              </div>
            </Grid>
            <Grid item xs={4} />
          </Grid>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
              Don't have an account?{' '}
              <Link
                to="/register"
                style={{
                  fontWeight: 600,
                  color: '#1e3c72',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/get-started', { 
                    replace: false,
                    state: { 
                      from: location.state?.from || '/',
                      timestamp: Date.now()
                    }
                  });
                }}
              >
                Sign up for free
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={parentConsentDialogOpen}
        onClose={() => setParentConsentDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Parent Consent Needed</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary' }}>
            {parentConsentMessage || 'We need parent or guardian consent before you can log in.'}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Would you like us to resend the consent email to your parent or guardian?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setParentConsentDialogOpen(false)} disabled={resendingParentConsent}>
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={handleResendParentConsent}
            disabled={resendingParentConsent || !email}
          >
            {resendingParentConsent ? 'Resending...' : 'Resend Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
